import { __decorate } from "tslib";
import React, { cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import defer from 'lodash/defer';
import noop from 'lodash/noop';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import classNames from 'classnames';
import classes from 'component-classes';
import { pxToRem } from '../../../lib/_util/UnitConvertor';
import KeyCode from '../../../lib/_util/KeyCode';
import { getConfig } from '../../../lib/configure';
import ViewComponent from '../core/ViewComponent';
import Icon from '../icon';
import autobind from '../_util/autobind';
import Button from '../button/Button';
import EventManager from '../_util/EventManager';
import isEmpty from '../_util/isEmpty';
import { ButtonColor, FuncType } from '../button/enum';
import asyncComponent from '../_util/AsyncComponent';
import message from '../message';
import exception from '../_util/exception';
import { $l } from '../locale-context';
import DataSetRequestError from '../data-set/DataSetRequestError';
import { suffixCls } from './utils';
function fixUnit(n) {
    if (isNumber(n)) {
        return `${n}px`;
    }
    return n;
}
function getTransformOrigin(position, style) {
    const { offsetWidth = 0, scrollTop = 0, scrollLeft = 0 } = typeof window === 'undefined' ? {} : document.documentElement;
    const { width = 520, left, top = 100 } = style;
    const { x, y } = position;
    const originX = `calc(${x}px - ${isNil(left) ? `(${offsetWidth}px - ${fixUnit(width)}) / 2` : `${fixUnit(left)}`} - ${scrollLeft}px)`;
    // const originX = isNil(left) ? `calc(${x}px - (${offsetWidth}px - ${width}px) / 2)` : `${x - (toPx(left) || 0)}px`;
    const originY = `calc(${y}px - ${fixUnit(top)} - ${scrollTop}px)`;
    // const originY = `${y - (toPx(top) || 0) - scrollTop}px`;
    return `${originX} ${originY}`;
}
export default class Modal extends ViewComponent {
    constructor() {
        super(...arguments);
        this.moveEvent = new EventManager(typeof window === 'undefined' ? undefined : document);
        this.okCancelEvent = new EventManager();
        this.saveCancelRef = node => (this.cancelButton = node);
        this.handleKeyDown = e => {
            const { cancelButton } = this;
            if (cancelButton && !cancelButton.isDisabled() && e.keyCode === KeyCode.ESC) {
                cancelButton.handleClickWait(e);
            }
        };
        this.getDefaultHeader = (title, closeButton, _okBtn, _cancelBtn) => {
            const { prefixCls } = this;
            if (title || closeButton) {
                return (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: `${prefixCls}-title` }, title),
                    React.createElement("div", { className: `${prefixCls}-header-buttons` }, closeButton)));
            }
        };
        this.getDefaultFooter = (okBtn, cancelBtn) => {
            const { okCancel, okButton, cancelButton, okFirst = getConfig('modalOkFirst'), drawer } = this.props;
            const buttons = [];
            if (okButton !== false) {
                buttons.push(okBtn);
            }
            if (okCancel || cancelButton) {
                const drawerOkFirst = getConfig('drawerOkFirst');
                if (drawer && !isNil(drawerOkFirst)) {
                    if (drawerOkFirst) {
                        buttons.push(cancelBtn);
                    }
                    else {
                        buttons.unshift(cancelBtn);
                    }
                }
                else if (okFirst) {
                    buttons.push(cancelBtn);
                }
                else {
                    buttons.unshift(cancelBtn);
                }
            }
            return React.createElement("div", null, buttons);
        };
        this.registerOk = ok => {
            this.okCancelEvent.removeEventListener('ok');
            this.okCancelEvent.addEventListener('ok', ok);
        };
        this.registerCancel = cancel => {
            this.okCancelEvent.removeEventListener('cancel');
            this.okCancelEvent.addEventListener('cancel', cancel);
        };
    }
    get okBtn() {
        const { okProps, okText = $l('Modal', 'ok'), drawer, } = this.props;
        const modalButtonProps = getConfig('modalButtonProps');
        const funcType = drawer
            ? FuncType.raised
            : getConfig('buttonFuncType');
        return (React.createElement(Button, Object.assign({ key: "ok", funcType: funcType, color: ButtonColor.primary, onClick: this.handleOk }, modalButtonProps, okProps), okText));
    }
    get cancelBtn() {
        const { cancelProps, cancelText = $l('Modal', 'cancel'), drawer, } = this.props;
        const modalButtonProps = getConfig('modalButtonProps');
        const funcType = drawer
            ? FuncType.raised
            : getConfig('buttonFuncType');
        return (React.createElement(Button, Object.assign({ key: "cancel", ref: this.saveCancelRef, funcType: funcType, onClick: this.handleCancel }, modalButtonProps, cancelProps), cancelText));
    }
    ;
    getOtherProps() {
        const otherProps = omit(super.getOtherProps(), [
            '__deprecate__',
            'closable',
            'movable',
            'maskClosable',
            'maskStyle',
            'mask',
            'maskClassName',
            'keyboardClosable',
            'fullScreen',
            'title',
            'footer',
            'close',
            'update',
            'okText',
            'cancelText',
            'okButton',
            'cancelButton',
            'okCancel',
            'onClose',
            'onOk',
            'onCancel',
            'destroyOnClose',
            'drawer',
            'drawerOffset',
            'drawerTransitionName',
            'transitionAppear',
            'afterClose',
            'okProps',
            'cancelProps',
            'border',
            'drawerBorder',
            'okFirst',
            'autoCenter',
            'mousePosition',
            'active',
            'contentStyle',
            'bodyStyle',
            'closeOnLocationChange',
        ]);
        const { hidden, mousePosition, keyboardClosable = getConfig('modalKeyboard'), style = {}, drawer } = this.props;
        if (keyboardClosable) {
            otherProps.autoFocus = true;
            otherProps.tabIndex = -1;
            otherProps.onKeyDown = this.handleKeyDown;
        }
        if (!drawer) {
            const position = this.mousePosition || mousePosition;
            if (position) {
                this.mousePosition = position;
                otherProps.style = {
                    ...style,
                    transformOrigin: getTransformOrigin(position, style),
                };
            }
            if (hidden) {
                this.mousePosition = undefined;
            }
        }
        return otherProps;
    }
    contentReference(node) {
        this.contentNode = node;
    }
    getClassName() {
        const { prefixCls, props: { style = {}, fullScreen, drawer, drawerTransitionName = getConfig('drawerTransitionName'), size, active, border = getConfig('modalSectionBorder'), drawerBorder = getConfig('drawerSectionBorder'), autoCenter = getConfig('modalAutoCenter'), }, } = this;
        return super.getClassName({
            [`${prefixCls}-center`]: !drawer && !('left' in style || 'right' in style) && !this.offset,
            [`${prefixCls}-fullscreen`]: fullScreen,
            [`${prefixCls}-drawer`]: drawer,
            [`${prefixCls}-border`]: drawer ? drawerBorder : border,
            [`${prefixCls}-drawer-${drawerTransitionName}`]: drawer,
            [`${prefixCls}-auto-center`]: autoCenter,
            [`${prefixCls}-${size}`]: size,
            [`${prefixCls}-active`]: active,
        });
    }
    render() {
        const { prefixCls, props: { contentStyle } } = this;
        const header = this.getHeader();
        const body = this.getBody();
        const footer = this.getFooter();
        return (React.createElement("div", Object.assign({}, this.getMergedProps()),
            React.createElement("div", { ref: this.contentReference, className: `${prefixCls}-content`, style: contentStyle },
                header,
                body,
                footer)));
    }
    componentWillUpdate({ hidden }) {
        if (hidden === false && hidden !== this.props.hidden) {
            defer(() => this.focus());
        }
    }
    componentWillUnmount() {
        this.moveEvent.clear();
        this.okCancelEvent.clear();
    }
    handleHeaderMouseDown(downEvent) {
        const { element, contentNode, props: { autoCenter = getConfig('modalAutoCenter') } } = this;
        if (element && contentNode) {
            const { prefixCls } = this;
            const { clientX, clientY } = downEvent;
            const { offsetLeft, offsetTop } = element;
            const heightW = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
            let autoMove = 0;
            this.moveEvent
                .addEventListener('mousemove', (moveEvent) => {
                const { clientX: moveX, clientY: moveY } = moveEvent;
                classes(element).remove(`${prefixCls}-center`);
                const left = pxToRem(Math.max(offsetLeft + moveX - clientX, 0));
                const top = pxToRem(Math.max(offsetTop + autoMove + moveY - clientY, 0));
                this.offset = [left, top];
                if (autoCenter && classes(element).has(`${prefixCls}-auto-center`)) {
                    classes(element).remove(`${prefixCls}-auto-center`);
                    autoMove = Math.max((heightW - contentNode.clientHeight) / 2, 0);
                    Object.assign(element.style, {
                        left,
                        top: pxToRem(autoMove),
                    });
                }
                else {
                    Object.assign(element.style, {
                        left,
                        top,
                    });
                }
            })
                .addEventListener('mouseup', () => {
                this.moveEvent.clear();
            });
        }
    }
    async handleOk() {
        const { onOk = noop } = this.props;
        const promise = Promise.all([onOk(), this.okCancelEvent.fireEvent('ok')]);
        try {
            const [ret1, ret2] = await promise;
            if (ret1 !== false && ret2) {
                this.close();
            }
        }
        catch (e) {
            if (!(e instanceof DataSetRequestError)) {
                message.error(exception(e));
            }
            throw e;
        }
    }
    async handleCancel() {
        const { onCancel = noop } = this.props;
        const promise = Promise.all([onCancel(), this.okCancelEvent.fireEvent('cancel')]);
        try {
            const [ret1, ret2] = await promise;
            if (ret1 !== false && ret2) {
                this.close();
            }
        }
        catch (e) {
            if (!(e instanceof DataSetRequestError)) {
                message.error(exception(e));
            }
            throw e;
        }
    }
    getHeader() {
        const { header = this.getDefaultHeader, title, } = this.props;
        if (typeof header === 'function') {
            const closeButton = this.getCloseButton();
            return this.getWrappedHeader(header(title, closeButton, this.okBtn, this.cancelBtn));
        }
        if (!isEmpty(header, true)) {
            return this.getWrappedHeader(header);
        }
    }
    getWrappedHeader(header) {
        const { prefixCls, props: { title, closable, movable, fullScreen, drawer }, } = this;
        if (title || closable || movable || header) {
            const headerProps = {
                className: classNames(`${prefixCls}-header`, {
                    [`${prefixCls}-movable`]: movable && !fullScreen && !drawer,
                    [`${prefixCls}-title-none`]: !title,
                }),
            };
            if (movable && !fullScreen && !drawer) {
                headerProps.onMouseDown = this.handleHeaderMouseDown;
            }
            return (React.createElement("div", Object.assign({}, headerProps), header));
        }
    }
    getCloseButton() {
        const { prefixCls, props: { closable }, } = this;
        if (closable) {
            return (React.createElement("button", { type: "button", className: `${prefixCls}-header-button`, onClick: this.handleCancel },
                React.createElement(Icon, { type: "close" })));
        }
    }
    getBody() {
        const { children } = this.props;
        return this.renderChildren(typeof children === 'function'
            ? asyncComponent(children)
            : children);
    }
    getFooter() {
        const { footer = this.getDefaultFooter, } = this.props;
        if (typeof footer === 'function') {
            return this.getWrappedFooter(footer(this.okBtn, this.cancelBtn));
        }
        if (!isEmpty(footer, true)) {
            return this.getWrappedFooter(footer);
        }
    }
    getWrappedFooter(footer) {
        const { prefixCls } = this;
        const { drawer } = this.props;
        const className = classNames(`${prefixCls}-footer`, {
            [`${prefixCls}-footer-drawer`]: !!drawer,
        });
        return React.createElement("div", { className: className }, footer);
    }
    renderChildren(children) {
        if (children) {
            const { prefixCls, props } = this;
            const { close = noop, update = noop, bodyStyle } = props;
            const modal = {
                close,
                update,
                props,
                handleOk: this.registerOk,
                handleCancel: this.registerCancel,
            };
            return (React.createElement("div", { className: `${prefixCls}-body`, style: bodyStyle }, isValidElement(children) ? cloneElement(children, { modal }) : children));
        }
    }
    close() {
        const { close = noop } = this.props;
        close();
    }
}
Modal.displayName = 'Modal';
Modal.propTypes = {
    ...ViewComponent.propTypes,
    closable: PropTypes.bool,
    movable: PropTypes.bool,
    fullScreen: PropTypes.bool,
    maskClosable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    maskStyle: PropTypes.object,
    mask: PropTypes.bool,
    maskClassName: PropTypes.string,
    keyboardClosable: PropTypes.bool,
    footer: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.bool]),
    destroyOnClose: PropTypes.bool,
    okText: PropTypes.node,
    cancelText: PropTypes.node,
    okProps: PropTypes.object,
    autoCenter: PropTypes.bool,
    cancelProps: PropTypes.object,
    onClose: PropTypes.func,
    onOk: PropTypes.func,
    onCancel: PropTypes.func,
    afterClose: PropTypes.func,
    okButton: PropTypes.bool,
    cancelButton: PropTypes.bool,
    okCancel: PropTypes.bool,
    drawer: PropTypes.bool,
    drawerOffset: PropTypes.number,
    drawerTransitionName: PropTypes.oneOf(['slide-up', 'slide-right', 'slide-down', 'slide-up', 'slide-left']),
    okFirst: PropTypes.bool,
    mousePosition: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
    contentStyle: PropTypes.object,
    bodyStyle: PropTypes.object,
    closeOnLocationChange: PropTypes.bool,
};
Modal.defaultProps = {
    suffixCls,
    closable: false,
    movable: true,
    mask: true,
    okButton: true,
    okCancel: true,
    destroyOnClose: true,
    fullScreen: false,
    drawer: false,
    drawerOffset: 150,
    autoFocus: true,
    closeOnLocationChange: true,
};
__decorate([
    autobind
], Modal.prototype, "contentReference", null);
__decorate([
    autobind
], Modal.prototype, "handleHeaderMouseDown", null);
__decorate([
    autobind
], Modal.prototype, "handleOk", null);
__decorate([
    autobind
], Modal.prototype, "handleCancel", null);
__decorate([
    autobind
], Modal.prototype, "close", null);
